import React, { useState, useRef, useEffect, useCallback, useId } from 'react';

import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { logout } from '@/services/auth';
import { useAuth, useSettings } from '@/stores/useAppStore';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { AboutModal, Icons } from '@shared/components/ui';
import { Z } from '@shared/constants/zIndex';

import AppearanceRow from './AppearanceRow';
import UsageMenuRow from './UsageMenuRow';

type MenuEntry =
  | { kind: 'item'; text: string; icon: React.ReactElement; onClick: () => void }
  | { kind: 'appearance' }
  | { kind: 'usage'; onClick: () => void };

interface MenuSection {
  /** Stable key — sections have no visible label any more. */
  id: 'account' | 'footer';
  entries: MenuEntry[];
}

interface MenuActions {
  navigateTo: (path: string) => void;
  openAbout: () => void;
  signOut: () => void;
  /** True inside any installed app — this menu is their only way back to Conway. */
  needsEscapeRoute: boolean;
}

/**
 * Assemble the profile menu: identity header (rendered by ProfileMenu) ·
 * Appearance · Settings · Usage · [Open Conway] · About · Sign Out. Assistant and
 * Administration left the menu in step 2 — they are All-areas cards. Kept
 * outside the hook so the hook stays under the cyclomatic-complexity cap.
 */
function buildMenuSections({
  navigateTo,
  openAbout,
  signOut,
  needsEscapeRoute,
}: MenuActions): MenuSection[] {
  const accountSection: MenuSection = {
    id: 'account',
    entries: [
      { kind: 'appearance' },
      {
        kind: 'item',
        text: 'Settings',
        icon: <Icons.Settings size="md" color="secondary" />,
        onClick: () => navigateTo('/settings/you/profile'),
      },
      { kind: 'usage', onClick: () => navigateTo('/settings/you/usage') },
    ],
  };

  const footerSection: MenuSection = {
    id: 'footer',
    entries: [
      ...(needsEscapeRoute
        ? [
            {
              // An installed app's only way back to the rest of Conway.
              // `location.assign` and not the router on purpose:
              // manifest scope is evaluated only on document-level navigations,
              // so a `navigate('/home')` renders Home INSIDE the installed app
              // still wearing its chrome — an escape hatch that doesn't escape.
              kind: 'item' as const,
              text: 'Open Conway',
              icon: <Icons.Home size="md" color="secondary" />,
              onClick: () => window.location.assign('/home'),
            },
          ]
        : []),
      {
        kind: 'item',
        text: 'About',
        icon: <Icons.Info size="md" color="secondary" />,
        onClick: openAbout,
      },
      {
        kind: 'item',
        text: 'Sign Out',
        icon: <Icons.Logout size="md" color="secondary" />,
        onClick: signOut,
      },
    ],
  };

  return [accountSection, footerSection];
}

/** Which way the menu opens from the avatar. */
type MenuPlacement = 'above' | 'below';

/** Where the portal menu sits: anchored up-and-left of the avatar, or down-and-right of it. */
type MenuPosition = { bottom: number; left: number } | { top: number; right: number };

interface SideNavProfileProps {
  isCollapsed: boolean;
  onNavigate?: (() => void) | undefined;
  /**
   * `above` (default): the rail's menu, opening upward from the avatar at the
   * bottom. `below`: the phone's top row — the menu drops under the avatar,
   * right-aligned to it, and always renders via the portal.
   */
  placement?: MenuPlacement;
}

// ---------------------------------------------------------------------------
// Hook: useProfileMenu
// ---------------------------------------------------------------------------

/**
 * Manages the profile dropdown menu state, position, and menu item configuration.
 *
 * Handles menu open/close toggling, click-outside dismissal, portal-based
 * positioning (collapsed rail, or any `below` placement), and assembles the
 * menu (Appearance · Settings · Usage / [Open Conway] · About · Sign Out — the identity
 * header comes from the user info returned here).
 *
 * @param isCollapsed - Whether the sidebar is in its narrow/collapsed state.
 *   When collapsed, the menu renders via a portal with fixed positioning.
 * @param onNavigate - Optional callback invoked after navigating to a route.
 * @param placement - `above` opens upward from the avatar; `below` drops under
 *   it, right-aligned, via the portal regardless of `isCollapsed`.
 * @returns An object containing menu open/close state, refs for positioning,
 *   user display info (name, email, initials), and the assembled menu sections.
 */
function useProfileMenu(
  isCollapsed: boolean,
  onNavigate: (() => void) | undefined,
  placement: MenuPlacement
) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const portalMenuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferredName } = useSettings();
  const installedApp = useInstalledApp();

  const userName = preferredName || user?.name || user?.email || 'User';
  const userEmail = user?.email || '';
  const initials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // The portal escapes overflow-hidden, so it is positioned from the button's rect.
  const usesPortal = isCollapsed || placement === 'below';

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition(
      placement === 'below'
        ? { top: rect.bottom + 8, right: window.innerWidth - rect.right }
        : { bottom: window.innerHeight - rect.top + 8, left: rect.left }
    );
  }, [placement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (portalMenuRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAndNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
    onNavigate?.();
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleOpenAbout = () => {
    setIsOpen(false);
    setIsAboutOpen(true);
  };

  const toggleMenu = () => {
    if (usesPortal) updateMenuPosition();
    setIsOpen(prev => !prev);
  };

  const menuSections = buildMenuSections({
    navigateTo: closeAndNavigate,
    openAbout: handleOpenAbout,
    signOut: handleLogout,
    // Every installed app: none of them carry an escape row any more.
    needsEscapeRoute: installedApp !== null,
  });

  return {
    isOpen,
    isAboutOpen,
    setIsAboutOpen,
    menuPosition,
    usesPortal,
    wrapperRef,
    portalMenuRef,
    buttonRef,
    userName,
    userEmail,
    initials,
    toggleMenu,
    menuSections,
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ProfileButtonProps {
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  isCollapsed: boolean;
  isOpen: boolean;
  initials: string;
  userName: string;
  userEmail: string;
  onToggle: () => void;
}

const ProfileButton: React.FC<ProfileButtonProps> = ({
  buttonRef,
  isCollapsed,
  isOpen,
  initials,
  userName,
  userEmail,
  onToggle,
}) => (
  <button
    ref={buttonRef}
    onClick={onToggle}
    className={`hover:bg-sidebar-accent flex w-full items-center gap-3 overflow-hidden rounded-sm px-3 py-2 whitespace-nowrap transition-colors ${isOpen ? 'bg-sidebar-accent' : ''} `}
    aria-label="User menu"
    aria-expanded={isOpen}
    aria-haspopup="true"
  >
    {/* Avatar */}
    <div className="bg-sidebar-brand text-sidebar-brand-foreground flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium">
      {initials}
    </div>

    {/* User info (when expanded). Capped to the avatar's height (h-8) and centered so the
        two-line name+email block never makes the button taller than the collapsed
        (avatar-only) state — otherwise the avatar's vertical center shifts on collapse. */}
    {!isCollapsed && (
      <div className="flex h-8 min-w-0 flex-1 flex-col justify-center overflow-hidden text-left">
        <p className="text-sidebar-foreground truncate text-sm leading-tight font-medium">
          {userName}
        </p>
        {userEmail && (
          <p className="text-sidebar-foreground/80 truncate text-xs leading-tight">{userEmail}</p>
        )}
      </div>
    )}

    {/* Chevron indicator (when expanded) */}
    {!isCollapsed && (
      <Icons.ChevronDown
        size="sm"
        color="inherit"
        className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    )}
  </button>
);

interface ProfileMenuProps {
  /** Id of the identity header; both menu containers label themselves by it. */
  headerId: string;
  menuSections: MenuSection[];
  userName: string;
  userEmail: string;
}

/** Identity header (non-interactive), then each section behind a divider. */
const ProfileMenu: React.FC<ProfileMenuProps> = ({
  menuSections,
  userName,
  userEmail,
  headerId,
}) => (
  <div className="py-1">
    <div id={headerId} className="px-4 pt-2 pb-2">
      <p className="text-foreground truncate text-sm font-medium">{userName}</p>
      {userEmail && <p className="text-muted-foreground truncate text-xs">{userEmail}</p>}
    </div>
    {menuSections.map(section => (
      <React.Fragment key={section.id}>
        <div role="separator" className="border-border my-1 border-t" />
        {section.entries.map(entry =>
          entry.kind === 'appearance' ? (
            <AppearanceRow key="appearance" />
          ) : entry.kind === 'usage' ? (
            <UsageMenuRow key="usage" onClick={entry.onClick} />
          ) : (
            <button
              key={entry.text}
              onClick={entry.onClick}
              className="text-foreground hover:bg-muted flex w-full items-center px-4 py-2 text-sm transition-colors"
              role="menuitem"
            >
              <span className="mr-3">{entry.icon}</span>
              {entry.text}
            </button>
          )
        )}
      </React.Fragment>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * SideNavProfile Component
 *
 * A sidebar-positioned profile section that displays user initials/avatar.
 * Opens a pop-up menu (upward in the rail, downward in the phone's top row):
 * identity header, the Appearance (Light / Dark / System) control, Settings,
 * Usage (with this month's spend), Open Conway inside installed apps, About,
 * and Sign Out.
 */
const SideNavProfile: React.FC<SideNavProfileProps> = ({
  isCollapsed,
  onNavigate,
  placement = 'above',
}) => {
  const headerId = useId();
  const {
    isOpen,
    isAboutOpen,
    setIsAboutOpen,
    menuPosition,
    usesPortal,
    wrapperRef,
    portalMenuRef,
    buttonRef,
    userName,
    userEmail,
    initials,
    toggleMenu,
    menuSections,
  } = useProfileMenu(isCollapsed, onNavigate, placement);

  return (
    <>
      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      <div className="relative" ref={wrapperRef}>
        <ProfileButton
          buttonRef={buttonRef}
          isCollapsed={isCollapsed}
          isOpen={isOpen}
          initials={initials}
          userName={userName}
          userEmail={userEmail}
          onToggle={toggleMenu}
        />

        {/* Pop-up menu (opens upward) -- in the expanded rail, render inline */}
        {isOpen && !usesPortal && (
          <div
            className="border-border bg-card absolute right-0 bottom-full left-0 mb-2 overflow-hidden rounded-sm border shadow-lg"
            style={{ zIndex: Z.DROPDOWN }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby={headerId}
          >
            <ProfileMenu
              headerId={headerId}
              menuSections={menuSections}
              userName={userName}
              userEmail={userEmail}
            />
          </div>
        )}
      </div>

      {/* Pop-up menu -- collapsed rail or `below`: render via portal to escape overflow-hidden.
          `menuPosition` carries whichever anchor the placement computed. */}
      {isOpen &&
        usesPortal &&
        menuPosition &&
        createPortal(
          <div
            ref={portalMenuRef}
            className="border-border bg-card fixed w-56 overflow-hidden rounded-sm border shadow-lg"
            style={{ ...menuPosition, zIndex: Z.DROPDOWN }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby={headerId}
          >
            <ProfileMenu
              headerId={headerId}
              menuSections={menuSections}
              userName={userName}
              userEmail={userEmail}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default SideNavProfile;
